import { InputField, InputSelectOptions, LoaderButton } from 'components/molecules'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { ListGroup, ListGroupItem, FormGroup, Label, Input, Button } from 'reactstrap'
import { SCOPE } from 'domains/outbound/deals/constants'

const parseDealStatesParam = (raw) => {
  if (raw == null) return []
  if (Array.isArray(raw)) {
    return raw.map((x) => (x || '').toString().trim()).filter(Boolean)
  }

  return (raw || '')
    .toString()
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
}

const stringifyDealStatesParam = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  return Array.from(new Set(arr.map((x) => (x || '').toString().trim()).filter(Boolean))).join(',')
}

const propTypes = {
  filterValues            : PropTypes.object.isRequired,
  onClickSubmitForm       : PropTypes.func.isRequired,
  onClickReset            : PropTypes.func.isRequired,
  createdByOptions        : PropTypes.array,
  canShowCreatedByFilter  : PropTypes.bool,
  enableDealStateFilter   : PropTypes.bool,
  dealStateOptions        : PropTypes.array,
  enablePaymentMethodFilter   : PropTypes.bool,
  paymentMethodOptions      : PropTypes.array,
  enableCreatedDealByFilter : PropTypes.bool,
  createdDealByOptions      : PropTypes.array,
}

const OutboundDealsFilterForm = ({
  filterValues,
  onClickSubmitForm,
  onClickReset,
  createdByOptions = [],
  canShowCreatedByFilter = false,
  enableDealStateFilter = false,
  dealStateOptions = [],
  enablePaymentMethodFilter = false,
  paymentMethodOptions = [],
  enableCreatedDealByFilter = false,
  createdDealByOptions = [],
}) => (
  <Formik
    initialValues={{
      name                : filterValues.name         || '',
      email               : filterValues.email        || '',
      phone               : filterValues.phone        || '',
      order_number        : filterValues.order_number || '',
      sku                 : filterValues.sku          || '',
      scope               : filterValues.scope        || SCOPE.NEED_PACKING,
      created_by_admin_id : filterValues.created_by_admin_id || '',
      created_deal_by     : filterValues.created_deal_by || '',
      pickup_day          : filterValues.pickup_day   || '',
      payment_method      : filterValues.payment_method || '',
      deal_states         : parseDealStatesParam(filterValues.deal_states),
    }}
    enableReinitialize
    onSubmit={(values, formikBag) => {
      onClickSubmitForm(
        {
          ...values,
          deal_states: stringifyDealStatesParam(values.deal_states),
        },
        formikBag
      )
    }}
  >
    {(formikProps) => (
      <Form>
        <ListGroup>

          <ListGroupItem>
            <InputField
              type="text"
              name="name"
              label="Customer Name"
              placeholder="Nama customer"
              bsSize="md"
            />
          </ListGroupItem>

          <ListGroupItem>
            <InputField
              type="text"
              name="email"
              label="Email"
              placeholder="Email"
              bsSize="md"
            />
          </ListGroupItem>

          {
            canShowCreatedByFilter && (
              <ListGroupItem>
                <FormGroup className="mb-0">
                  <Label>{ 'Created By' }</Label>
                  <Input
                    type='select'
                    name='created_by_admin_id'
                    value={ formikProps.values.created_by_admin_id }
                    onChange={ formikProps.handleChange }
                  >
                    <option value=''>{ 'All' }</option>
                    {
                      createdByOptions.map((admin) => (
                        <option key={ admin.id } value={ admin.id }>
                          { admin.email }
                        </option>
                      ))
                    }
                  </Input>
                </FormGroup>
              </ListGroupItem>
            )
          }

          {
            enableCreatedDealByFilter && createdDealByOptions.length > 0 && (
              <ListGroupItem>
                <FormGroup className="mb-0">
                  <Label>{ 'Create Deal By' }</Label>
                  <Input
                    type='select'
                    name='created_deal_by'
                    value={ formikProps.values.created_deal_by }
                    onChange={ formikProps.handleChange }
                  >
                    {
                      createdDealByOptions.map((option) => (
                        <option
                          key={ option.value || '__all_created_deal_by__' }
                          value={ option.value }
                        >
                          { option.label }
                        </option>
                      ))
                    }
                  </Input>
                </FormGroup>
              </ListGroupItem>
            )
          }

          {
            enablePaymentMethodFilter && paymentMethodOptions.length > 0 && (
              <ListGroupItem>
                <FormGroup className="mb-0">
                  <Label>{ 'Payment Method' }</Label>
                  <Input
                    type='select'
                    name='payment_method'
                    value={ formikProps.values.payment_method }
                    onChange={ formikProps.handleChange }
                  >
                    {
                      paymentMethodOptions.map((option) => (
                        <option
                          key={ option.value || '__all_payment_method__' }
                          value={ option.value }
                        >
                          { option.label }
                        </option>
                      ))
                    }
                  </Input>
                </FormGroup>
              </ListGroupItem>
            )
          }

          {
            enableDealStateFilter && (
              <ListGroupItem>
                <FormGroup className="mb-0">
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <Label className="mb-0">{ 'Deal State' }</Label>

                    <div className='d-flex align-items-center' style={{ gap: 8 }}>
                      <Button
                        type='button'
                        color='link'
                        size='sm'
                        className='p-0'
                        onClick={() => {
                          formikProps.setFieldValue(
                            'deal_states',
                            dealStateOptions.map((o) => o.value)
                          )
                        }}
                      >
                        Select All
                      </Button>

                      <Button
                        type='button'
                        color='link'
                        size='sm'
                        className='p-0'
                        onClick={() => {
                          formikProps.setFieldValue('deal_states', [])
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>

                  <div className='border rounded p-2' style={{ background: '#fff' }}>
                    {dealStateOptions.map((option) => {
                      const id = `deal_state_${option.value}`
                      const checked = (formikProps.values.deal_states || []).includes(option.value)

                      return (
                        <div key={option.value} className='form-check'>
                          <input
                            id={id}
                            className='form-check-input'
                            type='checkbox'
                            checked={checked}
                            onChange={() => {
                              const current = Array.isArray(formikProps.values.deal_states)
                                ? formikProps.values.deal_states
                                : []

                              const next = checked
                                ? current.filter((x) => x !== option.value)
                                : [ ...current, option.value ]

                              formikProps.setFieldValue('deal_states', next)
                            }}
                          />
                          <label className='form-check-label' htmlFor={id}>
                            {option.label}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </FormGroup>
              </ListGroupItem>
            )
          }

          <ListGroupItem>
            <FormGroup className="mb-0">
              <Label>{ 'Pickup Date' }</Label>
              <Input
                type='select'
                name='pickup_day'
                value={ formikProps.values.pickup_day }
                onChange={ formikProps.handleChange }
              >
                <option value=''>{ 'All' }</option>
                <option value='yesterday'>{ 'Yesterday + Older' }</option>
                <option value='today'>{ 'Today' }</option>
                <option value='tomorrow'>{ 'Tomorrow + Newer' }</option>
                <option value='unscheduled'>{ 'No Pickup Date' }</option>
              </Input>
            </FormGroup>
          </ListGroupItem>

          <ListGroupItem>
            <InputField
              type="text"
              name="phone"
              label="Phone Number"
              placeholder="No. HP (UserDetail)"
              bsSize="md"
            />
          </ListGroupItem>

          <ListGroupItem>
            <InputField
              type="text"
              name="order_number"
              label="Order Number"
              placeholder="BNK..."
              bsSize="md"
            />
          </ListGroupItem>

          <ListGroupItem>
            <InputField
              type="text"
              name="sku"
              label="SKU (Item Dalam Deal)"
              placeholder="SKU terakhir / parsial"
              bsSize="md"
            />
          </ListGroupItem>

          <ListGroupItem>
            <InputField
              type="select"
              name="scope"
              label="Status"
              bsSize="md"
            >
              <InputSelectOptions options={SCOPE} />
            </InputField>
          </ListGroupItem>

          <div className="d-flex justify-content-center position-sticky fixed-bottom bg-white py-3">
            <LoaderButton
              color="primary"
              className="mt-auto"
              type="submit"
              disabled={formikProps.isSubmitting}
              isLoading={formikProps.isSubmitting}
              text="Filter"
            />
            <LoaderButton
              color="danger"
              disabled={formikProps.isSubmitting}
              isLoading={formikProps.isSubmitting}
              text="Reset"
              onClick={onClickReset}
            />
          </div>

        </ListGroup>
      </Form>
    )}
  </Formik>
)

OutboundDealsFilterForm.propTypes = propTypes
export default OutboundDealsFilterForm
